/**
 * Money travels in API payloads as `1234.56` and lives everywhere else as integer cents, so no
 * fiscal sum ever touches binary floating point (Article III).
 */
/** Exact conversion. Accepts a JS number only because JSON delivers one; it is re-read as text. */
export function decimalToCents(value) {
    const text = typeof value === "number" ? numberText(value) : value.trim();
    const match = /^(-)?(\d+)(?:\.(\d{1,2}))?$/.exec(text);
    if (!match) {
        throw new Error(`"${String(value)}" no es un importe válido. Usá punto decimal: 1234.56.`);
    }
    const cents = Number(match[2]) * 100 + Number((match[3] ?? "").padEnd(2, "0"));
    if (!Number.isSafeInteger(cents))
        throw new Error(`El importe ${text} es demasiado grande.`);
    return match[1] ? -cents : cents;
}
function numberText(value) {
    if (!Number.isFinite(value))
        throw new Error("El importe no es un número.");
    // A double like 0.29 prints as "0.29"; toFixed(2) would also turn 1.005 into "1.00", hiding a
    // third decimal. Printing with up to 12 places and trimming keeps a third decimal visible so
    // the regex above rejects it instead of silently rounding.
    return value.toFixed(12).replace(/\.?0+$/, "");
}
/**
 * How far a voucher's total may sit from the sum of its lines. Real ARCA exports (Mis
 * Comprobantes, Sept. 2026) carry totals up to 5 cents off neto + IVA, and Total IVA one cent off
 * its per-rate columns: ARCA rounds each column on its own. Ten cents leaves margin for that and
 * has no fiscal effect — the IVA position is built from the lines, not from the total.
 */
export const ROUNDING_TOLERANCE_CENTS = 10;
/**
 * Cents as es-AR text ("1.234,56", "-0,50"), exactly: integer arithmetic on BigInt, never a
 * division by 100 in floating point, which drops a cent above ~90 billion pesos (Codex 009
 * round 3). Screens and PDFs that show fiscal amounts use this.
 */
export function formatCentsAr(cents) {
    if (!Number.isSafeInteger(cents))
        throw new Error(`El importe ${cents} no es un entero exacto.`);
    const value = BigInt(cents);
    const abs = value < 0n ? -value : value;
    const pesos = (abs / 100n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    const centavos = (abs % 100n).toString().padStart(2, "0");
    return `${value < 0n ? "-" : ""}${pesos},${centavos}`;
}
export function centsToDecimal(cents) {
    const sign = cents < 0 ? "-" : "";
    const abs = Math.abs(cents);
    return `${sign}${Math.trunc(abs / 100)}.${String(abs % 100).padStart(2, "0")}`;
}
export function sumCents(values) {
    return values.reduce((acc, v) => acc + v, 0);
}
const MICROS = 1000000n;
/** Exact decimal text (up to 6 places) → millionths. */
function rateMicros(rate) {
    const match = /^(\d+)(?:\.(\d{1,6}))?$/.exec(rate.trim());
    if (!match)
        throw new Error(`Tipo de cambio inválido: ${rate}.`);
    return BigInt(match[1] ?? "0") * MICROS + BigInt((match[2] ?? "").padEnd(6, "0"));
}
/**
 * Converts an amount in the voucher's currency to pesos at its exchange rate, in integer
 * arithmetic, rounding half away from zero. Identity for a rate of exactly 1.
 */
export function toPesosCents(cents, exchangeRate) {
    const micros = rateMicros(exchangeRate);
    if (micros === MICROS)
        return cents;
    const product = BigInt(cents) * micros;
    const sign = product < 0n ? -1n : 1n;
    return Number(((product * sign + MICROS / 2n) / MICROS) * sign);
}
/**
 * Exact money for sums that may outgrow a double (spec 015 — a studio's receivables over years):
 * decimal text ↔ BigInt cents, no floating point anywhere.
 */
export function decimalToBigCents(value) {
    const match = /^(-)?(\d+)(?:\.(\d{1,2}))?$/.exec(value.trim());
    if (!match)
        throw new Error(`"${value}" no es un importe válido. Usá punto decimal: 1234.56.`);
    const cents = BigInt(match[2] ?? "0") * 100n + BigInt((match[3] ?? "").padEnd(2, "0"));
    return match[1] ? -cents : cents;
}
export function bigCentsToDecimal(cents) {
    const abs = cents < 0n ? -cents : cents;
    return `${cents < 0n ? "-" : ""}${(abs / 100n).toString()}.${(abs % 100n).toString().padStart(2, "0")}`;
}
/**
 * Decimal text («-1234567.8») as es-AR («-1.234.567,80»), digit by digit: exact at any size, for
 * sums kept in BigInt (plan 017). Throws on anything that is not a decimal.
 */
export function formatDecimalAr(value) {
    const match = /^(-)?(\d+)(?:\.(\d{1,2}))?$/.exec(value.trim());
    if (!match)
        throw new Error(`"${value}" no es un importe válido. Usá punto decimal: 1234.56.`);
    const int = (match[2] ?? "0").replace(/^0+(?=\d)/, "").replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    const frac = (match[3] ?? "").padEnd(2, "0");
    const negative = match[1] !== undefined && /[1-9]/.test(`${match[2]}${frac}`);
    return `${negative ? "-" : ""}${int},${frac}`;
}
//# sourceMappingURL=money.js.map